/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ApplicationView, BuilderOfficeData, SystemRoleBit, UserType } from '@gh/builder-shared-data';

import { CommonUserContextService, ConfigurationHttpRequestInterceptor } from '@gh/common-shared';
import { MasterZoneData } from '@gh/common-shared-data';
import { REGION_CODE } from '@gh/config';
import { AuthService, Permission, PermissionNameSetProvider } from '@gh/core-auth';
import { Enum } from '@gh/core-data';
import { setupCurrentTimeSync } from '@gh/core-util';

import { BuilderUserContext, BuilderUserData } from './builder-user-context';
import { BuilderUserProfileStore } from './builder-user-profile-store.service';

@Injectable()
export class BuilderUserContextService extends CommonUserContextService<BuilderUserData> implements BuilderUserContext {
  private switched: boolean;

  constructor(authService: AuthService,
              userProfileStore: BuilderUserProfileStore,
              protected permissionProvider: PermissionNameSetProvider,
              private configurationHttpRequestInterceptor: ConfigurationHttpRequestInterceptor) {
    super(authService, userProfileStore);
  }

  get userType(): UserType {
    if (!this.user) {
      return this.userNotInitialized();
    }

    return this.user.type;
  }

  get sandbox(): boolean {
    return this.getInitParam('sandbox');
  }

  get applicationView(): ApplicationView {
    if (!this.user) {
      return this.userNotInitialized();
    }

    return this.user.appView;
  }

  get officeView(): boolean {
    return this.applicationView === ApplicationView.Office;
  }

  get corporateView(): boolean {
    return this.applicationView === ApplicationView.Corporate;
  }

  get masterView(): boolean {
    return this.applicationView === ApplicationView.Master;
  }

  get office(): Maybe<BuilderOfficeData> {
    if (!this.user) {
      return this.userNotInitialized();
    }

    return this.user.office;
  }

  get masterZone(): Maybe<MasterZoneData> {
    if (!this.user) {
      return this.userNotInitialized();
    }

    return this.user.masterZone;
  }

  get colorScheme(): string {
    if (!this.user) {
      return this.userNotInitialized();
    }

    return this.user.colorScheme;
  }

  isSwitched(): boolean {
    if (!this.user) {
      return this.userNotInitialized();
    }

    return this.switched;
  }

  hasPermission(permission: Permission): boolean {
    if (this.applicationView === ApplicationView.Office) {
      return super.hasPermission(permission);
    } else {
      return true;
    }
  }

  hasSystemRole(role: SystemRoleBit): boolean {
    if (!this.user) {
      return this.userNotInitialized();
    }

    return this.user.systemRoles.has(role);
  }

  changeColorScheme(scheme: string): void {
    if (!this.user) {
      return this.userNotInitialized();
    }

    this.user.colorScheme = scheme;
  }

  protected updateUserData(user: Maybe<BuilderUserData>): void {
    switch (this.userType) {
      case UserType.Corporate:
        this.switched = this.applicationView !== ApplicationView.Corporate;
        break;
      case UserType.Master:
        this.switched = this.applicationView !== ApplicationView.Master;
        break;
      default:
        this.switched = false;
        break;
    }

    if (user && this.applicationView === ApplicationView.Office) {
      if (this.switched) {
        this.permissionProvider.init(user.modulePermissions || []);
      } else {
        this.permissionProvider.init((user.permissions || []));
      }
    } else {
      this.permissionProvider.init([]);
    }

    if (user) {
      setupCurrentTimeSync({
        clientTime: new Date(),
        serverTime: user.currentDateTime,
      });
    }
  }

  protected setupHttpHeaders(params: Hash<any>): void {
    const appView: ApplicationView = params['appView'];

    const headers = {
      'X-Region': REGION_CODE,
    };

    if (appView) {
      const applicationView = appView;
      const applicationViewId = params['appViewId'];

      headers['X-AppView'] = Enum.toValue(ApplicationView, applicationView);

      switch (appView) {
        case ApplicationView.Master:
          headers['X-MasterZoneId'] = applicationViewId;
          break;
        case ApplicationView.Office:
          headers['X-OfficeId'] = applicationViewId;
          break;
        default:
          break;
      }
    }

    if (params['sandbox']) {
      headers['X-SandBox-Mode'] = true;
    }

    this.configurationHttpRequestInterceptor.headers = headers;
  }
}
